import React, { useContext, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import BaseTable from '../../components/shared/table/BaseTable';
import ToolbarButton from '../../components/shared/toolbar/ToolbarButton';
import { useColumns } from './hooks/useColumns';
import ContactListToolbar from './ContactListToolbar';
import ContactCard from './ContactCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { TableContext } from '../../components/shared/table/context/TableContext';

const ContactListTable = ({
  contacts = [],
  loading = false,
  error = null,
  filterType = '',
  onFilterTypeChange,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const { setColumns, filterRows } = useContext(TableContext);
  
  const handleRowClick = (rowId) => {
    navigate(`/contacts/${rowId}${userId ? `?userId=${userId}` : ''}`);
  };
  const columns = useColumns(contacts);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const filteredContacts = filterRows(contacts);

  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ContactListToolbar
        title="Contacts"
        columns={columns}
        data={contacts}
        showQuickFilter={true}
        quickFilterProps={{ debounceMs: 300 }}
        filterType={filterType}
        onFilterTypeChange={onFilterTypeChange}
        actionButton={
          <Link to={`/contacts/new${userId ? `?userId=${userId}` : ''}`}>
            <ToolbarButton
              variant="contained"
              startIcon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              }
            >
              Add Contact
            </ToolbarButton>
          </Link>
        }
      />
      <div className="flex-1 min-h-0">
        {isMobile ? (
          <div className="p-4 space-y-4 bg-gray-50">
            {filteredContacts.map(contact => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onRowClick={handleRowClick}
              />
            ))}
          </div>
        ) : (
          <BaseTable
            data={filteredContacts}
            columns={columns}
            emptyMessage="No contacts found. Add your first contact to get started."
            showFooter={true}
            onRowClick={handleRowClick}
          />
        )}
      </div>
    </div>
  );
};

export default ContactListTable;
